import { useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { AnchorButton } from '@formstack/phoenix/components/Button/Button';
import Icon from '@formstack/phoenix/components/Icon';
import { ActionVariants } from '@formstack/phoenix/components/actionVariants/constants';
import { Colors } from '@formstack/phoenix/components/colors';
import { ElementSizeVariants } from '@formstack/phoenix/components/componentSizeVariants/constants';
import { IconName } from '@formstack/phoenix/components/icons/constants';
import UnifiedHeader, { BuildEnvs, IUnifiedHeader, Products } from '@formstack/unified-header';
import '@formstack/unified-header/dist/unifiedHeader.css';

import DefaultAvatar from '@/assets/graphics/default-avatar.png';
import { ErrorBoundary } from '@/common/components/ErrorBoundary';
import {
  APP_CONNECTIONS_PATH,
  APP_PATHS,
  HOME_PATH,
  WORKFLOWS_PATH,
  WORKSPACE_PATH,
} from '@/common/constants';
import { useResource } from '@/common/resources';
import { isMainRouteMatchForLocation } from '@/common/utils/route-helpers';
import { USER_LOGOUT_URL } from '@/env-config';
import {
  getOrganizationDetails,
  getUserCopilotRole,
  getUserPlatformRoles,
  getUserTokens,
  isUserOrganizationAdmin,
  isUserParticipant,
  useGetUserInfoQuery,
  userHasAccessTo,
} from '@/store/auth';

import formstackLogo from './images/formstackLogo.svg';

const Logout = (
  <AnchorButton
    dataTest="logout-link"
    targetUrl={USER_LOGOUT_URL}
    variant={ActionVariants.link}
    buttonText="Log Out"
    buttonSize={ElementSizeVariants.small}
  />
);

export const Masthead = () => {
  const { appConnectionsLabel, homeLabel, workflowsLabel, workspaceLabel } = useResource(
    (resource) => ({
      appConnectionsLabel: resource.NAV.APP.appConnections,
      homeLabel: resource.NAV.MAIN.home,
      workflowsLabel: resource.NAV.MAIN.workflows,
      workspaceLabel: resource.NAV.MAIN.workspace,
    })
  );

  const location = useLocation();
  const navigate = useNavigate();

  const { data: userInfo } = useGetUserInfoQuery();

  const unifiedHeaderProps: IUnifiedHeader = useMemo(() => {
    const billingManagedByFsid = !!userInfo?.organization?.billingIsManagedByFsid;
    const isOrgAdmin = isUserOrganizationAdmin(userInfo);
    const isParticipant = isUserParticipant(userInfo);
    const hasWorkspaceAccess = userHasAccessTo('workspace', userInfo);
    const rootNavigationHref = isParticipant ? WORKSPACE_PATH : WORKFLOWS_PATH;

    return {
      appNavigation: isParticipant
        ? undefined
        : [
            {
              href: APP_CONNECTIONS_PATH,
              label: appConnectionsLabel,
              action: () => navigate(APP_CONNECTIONS_PATH),
            },
          ],
      enableUnifiedMessaging: billingManagedByFsid && process.env.COPILOT_BUILD_ENV !== 'local',
      env: process.env.COPILOT_BUILD_ENV as BuildEnvs,
      logout: {
        customNode: Logout,
      },
      mainNavigation: [
        ...(isParticipant
          ? []
          : [
              {
                href: HOME_PATH,
                label: homeLabel,
                action: () => navigate(HOME_PATH),
                selected: isMainRouteMatchForLocation(HOME_PATH, APP_PATHS, location),
              },
              {
                href: WORKFLOWS_PATH,
                label: workflowsLabel,
                action: () => navigate(WORKFLOWS_PATH),
                selected: isMainRouteMatchForLocation(WORKFLOWS_PATH, APP_PATHS, location),
              },
            ]),
        {
          href: hasWorkspaceAccess ? WORKSPACE_PATH : undefined,
          disabled: !hasWorkspaceAccess,
          icon: !hasWorkspaceAccess && (
            <Icon icon={IconName['lock-closed']} color={Colors['phx-gray-4']} />
          ),
          label: workspaceLabel,
          action: hasWorkspaceAccess ? () => navigate(WORKSPACE_PATH) : undefined,
          selected: isMainRouteMatchForLocation(WORKSPACE_PATH, APP_PATHS, location),
        },
      ],
      managedByAdministration: {
        accountSettings: isOrgAdmin,
        billing: isOrgAdmin && billingManagedByFsid,
        securitySettings: isOrgAdmin,
        userManagement: isOrgAdmin,
      },
      membershipId: userInfo?.membershipId,
      organization: getOrganizationDetails(userInfo),
      productLauncher: {
        activeProducts:
          getUserPlatformRoles(userInfo)?.map((role) => role.productId as Products) ?? [],
      },
      productKey: Products.Copilot,
      rootNavigation: { href: rootNavigationHref },
      tokens: getUserTokens(userInfo),
      user: {
        avatarUrl: userInfo?.picture ?? DefaultAvatar,
        email: userInfo?.email ?? '',
        fullName:
          (userInfo?.name && `${userInfo.name.first} ${userInfo.name.last}`) ?? 'Guest User',
        role: getUserCopilotRole(userInfo),
      },
      whiteLabel: matchPath(HOME_PATH, location.pathname)
        ? {
            href: rootNavigationHref,
            imageSrc: formstackLogo,
            name: 'Formstack by Intellistack Logo',
            height: '48px',
          }
        : undefined,
    };
  }, [
    appConnectionsLabel,
    homeLabel,
    location,
    navigate,
    userInfo,
    workflowsLabel,
    workspaceLabel,
  ]);

  return (
    <ErrorBoundary>
      <UnifiedHeader {...unifiedHeaderProps} />
    </ErrorBoundary>
  );
};
